import { useEffect, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";

import { FormControl, FormLabel, Grid2 as Grid } from "@mui/material";

import { RootState } from "@app/store";
import { getSelectedCustomerId } from "@features/customers/customerSlice";
import { formControlStyle, formLabelStyle } from "@features/common/styles";
import { useAppDispatch } from "@features/common/StoreHook";
import SortTable, { IProperty } from "@features/common/Table/SortTable";
import AddDeleteButtonGroup from "@features/common/ButtonGroups/AddDeleteButtonGroup";
import ExportExcel from "@features/common/ExportExcel";
import {
  fetchSecrets,
  getAllSecretsByResource,
  selectSecretsStatus,
  getSelectedSecretId,
  selectSecret,
} from "./secretsSlice";
import { ISecret } from "./types";
import { AddSecretDialog } from "./AddSecretDialog";
import { permissionsApi } from "@features/authorization/authService";
import { checkPermissions } from "@features/authorization/types";
import { toast } from "react-toastify";

/**
 *
 * In Secrets view, this is a table that lists available secrets in keyvault(s).
 * Table pagination enabled & "infinite scroll" enabled
 *
 *
 */

export const SecretSelector = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const customerId = useSelector(getSelectedCustomerId);
  const secrets: ISecret[] = useSelector(
    (state: RootState) => getAllSecretsByResource(state),
    shallowEqual,
  );
  // If no resource is selected, we can assume that the first resource is selected
  const { data } = permissionsApi.useGetCustomerPermissionsQuery(customerId);
  const secretPermissions = checkPermissions("secrets", data);
  const isLoading = useSelector(selectSecretsStatus) === "loading";
  const selectedSecretId: string | undefined = useSelector(getSelectedSecretId);
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);

  const handleRadioButtonChange = (id: string) => {
    dispatch(selectSecret(id));
  };

  const secretProperties: IProperty<ISecret>[] = [
    { key: "name", label: t("label-secret-name") },
    { key: "type", label: t("label-secret-type") },
  ];

  useEffect(() => {
    dispatch(fetchSecrets(customerId));
    dispatch(selectSecret(""));
  }, []);

  const handleAdd = () => {
    secretPermissions.create
      ? setAddDialogOpen(true)
      : toast.error("No create permission");
  };

  const mappedSecrets = secrets.map((secret) => {
    let secretType = secret.type;
    if (secret.location.cloud_platform.toLowerCase() === "azure") {
      secretType = secret.keyvault_secret_type || secret.type;
    }
    return {
      ...secret,
      type: secretType,
    };
  });

  return (
    <>
      <FormControl component="fieldset" sx={formControlStyle}>
        <FormLabel component="legend" sx={formLabelStyle}>
          {t("title-secrets")}
        </FormLabel>
        {!isLoading && (
          <Grid
            container
            justifyContent="flex-end"
            style={{ paddingBottom: "1rem" }}
          >
            <ExportExcel
              data={secrets}
              fileName="secrets"
              coloredButton={true}
            />
          </Grid>
        )}
        <SortTable
          properties={secretProperties}
          idProperty={"secret_id"}
          data={mappedSecrets}
          isLoading={isLoading}
          selectedId={selectedSecretId}
          handleChange={handleRadioButtonChange}
          pagination={true}
        />
        <AddDeleteButtonGroup
          disabled={false}
          handleAdd={handleAdd}
          //handleDelete={() => {}}
        />
        <AddSecretDialog
          onCancel={() => setAddDialogOpen(false)}
          customerId={customerId}
          show={addDialogOpen}
        />
      </FormControl>
    </>
  );
};
